import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  cloneApprovalRequestFilters,
  EMPTY_APPROVAL_REQUEST_FILTERS,
} from "@/lib/approvalRequests";
import { FAILURE_TYPE_OPTIONS } from "@/lib/failedInvoiceTypes";
import { getProcessingStatusColor, getReviewStatusColor } from "@/lib/semanticColors";
import { PROCESSING_STATUSES, REVIEW_STATUSES } from "@/lib/statusEnums";
import {
  useGetProcessingStatusesQuery,
  useGetReviewStatusesQuery,
  useGetReviewTypesQuery,
} from "@/services/invoices/invoicesApi";
import { useGetUsersQuery } from "@/services/users/usersApi";
import { cn } from "@/lib/utils";

const fieldClass =
  "num h-9 w-full min-w-0 rounded-[0.8rem] border-line bg-background shadow-none";

/**
 * Option lists come from the API so the panel offers exactly what the server
 * recognises; the static enums are the fallback for the moment before the
 * requests resolve, and keep the colours consistent either way.
 *
 * Review types have no static fallback - nothing in the app knows them ahead of
 * time, which is why `/invoices/review-types` had never been called before.
 */
function useOptionLists(open) {
  const skip = { skip: !open };
  const { data: reviewStatuses } = useGetReviewStatusesQuery(undefined, skip);
  const { data: processingStatuses } = useGetProcessingStatusesQuery(undefined, skip);
  const { data: reviewTypes } = useGetReviewTypesQuery(undefined, skip);

  return useMemo(
    () => ({
      review: reviewStatuses?.length
        ? reviewStatuses.map((status) => ({
            id: status.id,
            label: status.name,
            color: getReviewStatusColor(status.id),
          }))
        : REVIEW_STATUSES,
      processing: processingStatuses?.length
        ? processingStatuses.map((status) => ({
            id: status.id,
            label: status.name,
            color: getProcessingStatusColor(status.id),
          }))
        : PROCESSING_STATUSES,
      reviewType: (reviewTypes ?? []).map((type) => ({
        id: type.id,
        label: type.name ?? type.label ?? `Type ${type.id}`,
        color: null,
      })),
    }),
    [reviewStatuses, processingStatuses, reviewTypes],
  );
}

function toggleValue(values, value, checked) {
  if (checked) return values.includes(value) ? values : [...values, value];
  return values.filter((item) => item !== value);
}

function FilterSection({ title, hint, children }) {
  return (
    <section className="filter-offcanvas-section">
      <div className="filter-offcanvas-section-head">
        <h3 className="filter-offcanvas-section-title">{title}</h3>
        {hint && <p className="filter-offcanvas-section-hint">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

/** Multi-select list, matching the invoice filter panel's status checklists. */
function OptionChecklist({ legend, options = [], selected = [], onToggle }) {
  if (!options.length) return null;

  return (
    <fieldset className="filter-status-group">
      <legend className="filter-offcanvas-group-label">{legend}</legend>
      <div className="filter-status-grid">
        {options.map((option) => {
          const checked = selected.includes(option.id);
          const inputId = `approval-${legend}-${option.id}`
            .replace(/\s+/g, "-")
            .toLowerCase();
          return (
            <label
              key={option.id}
              htmlFor={inputId}
              className={cn("filter-status-option", checked && "filter-status-option-active")}
            >
              <Checkbox
                id={inputId}
                checked={checked}
                onCheckedChange={(value) => onToggle(option.id, value === true)}
              />
              {option.color && (
                <span className="status-dot shrink-0" style={{ backgroundColor: option.color }} />
              )}
              <span className="min-w-0 truncate">{option.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function UserSelect({ id, label, value, users, onChange, anyLabel }) {
  return (
    <div className="filter-range-field">
      <label htmlFor={id} className="filter-range-label">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={fieldClass}
      >
        <option value="">{anyLabel}</option>
        {users.map((user) => (
          <option key={user.guid ?? user.id} value={user.id}>
            {user.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function RangeFieldGroup({
  legend,
  startId,
  endId,
  startLabel,
  endLabel,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  inputType = "date",
}) {
  const numeric = inputType === "number";

  return (
    <fieldset className="filter-range-group">
      <legend className="filter-offcanvas-group-label">{legend}</legend>
      <div className="filter-range-row">
        <div className="filter-range-field">
          <label htmlFor={startId} className="filter-range-label">
            {startLabel}
          </label>
          <Input
            id={startId}
            type={inputType}
            min={numeric ? 0 : undefined}
            step={numeric ? "0.01" : undefined}
            inputMode={numeric ? "decimal" : undefined}
            placeholder={numeric ? "0.00" : undefined}
            value={startValue}
            onChange={(event) => onStartChange(event.target.value)}
            className={fieldClass}
          />
        </div>
        <span className="filter-range-sep" aria-hidden="true">
          to
        </span>
        <div className="filter-range-field">
          <label htmlFor={endId} className="filter-range-label">
            {endLabel}
          </label>
          <Input
            id={endId}
            type={inputType}
            min={numeric ? 0 : undefined}
            step={numeric ? "0.01" : undefined}
            inputMode={numeric ? "decimal" : undefined}
            placeholder={numeric ? "0.00" : undefined}
            value={endValue}
            onChange={(event) => onEndChange(event.target.value)}
            className={fieldClass}
          />
        </div>
      </div>
    </fieldset>
  );
}

export function ApprovalRequestsFilterOffcanvas({ open, onOpenChange, value, onApply }) {
  const [draft, setDraft] = useState(() => cloneApprovalRequestFilters(value));
  const options = useOptionLists(open);
  // Only fetch the people list while the panel that uses it is open.
  const { data: users = [] } = useGetUsersQuery(undefined, { skip: !open });

  const [wasOpen, setWasOpen] = useState(open);

  // Seed the draft from the applied filters each time the panel opens.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(cloneApprovalRequestFilters(value));
  }

  const set = (key) => (next) => setDraft((current) => ({ ...current, [key]: next }));
  const toggleList = (key) => (id, checked) =>
    setDraft((current) => ({ ...current, [key]: toggleValue(current[key] ?? [], id, checked) }));

  const apply = () => {
    onApply(cloneApprovalRequestFilters(draft));
    onOpenChange(false);
  };

  const clear = () => {
    setDraft(cloneApprovalRequestFilters(EMPTY_APPROVAL_REQUEST_FILTERS));
    onApply(cloneApprovalRequestFilters(EMPTY_APPROVAL_REQUEST_FILTERS));
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        overlayClassName="filter-offcanvas-veil"
        className="filter-offcanvas"
      >
        <div className="filter-offcanvas-panel">
          <SheetHeader className="filter-offcanvas-header">
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-base font-semibold tracking-tight">
                Filter requests
              </SheetTitle>
              <SheetDescription>
                Narrow the approval queue by amount, people, dates or status.
              </SheetDescription>
            </div>
            <SheetClose
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="filter-offcanvas-close shadow-none"
                />
              }
            >
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </SheetClose>
          </SheetHeader>

          <div className="filter-offcanvas-body space-y-5">
            {/* Amount leads: it is the reason most people open this panel on an
                approvals queue, and it is what the High value chip shortcuts. */}
            <FilterSection title="Amount" hint="Invoice total, in the workspace currency.">
              <RangeFieldGroup
                legend="Total"
                startId="approval-total-min"
                endId="approval-total-max"
                startLabel="Min"
                endLabel="Max"
                startValue={draft.totalMin}
                endValue={draft.totalMax}
                onStartChange={set("totalMin")}
                onEndChange={set("totalMax")}
                inputType="number"
              />
            </FilterSection>

            {/* Stacked, not a range row: `.filter-range-row` is a three-column
                grid built for field / "to" / field, so a third control lands in
                the narrow separator column. Names need the full width anyway. */}
            <FilterSection title="People" hint="Who the invoice belongs to, and who touched it.">
              <div className="filter-offcanvas-stack">
                <UserSelect
                  id="approval-assignee"
                  label="Assigned to"
                  anyLabel="Anyone"
                  value={draft.assignedToId}
                  users={users}
                  onChange={set("assignedToId")}
                />
                <UserSelect
                  id="approval-created-by"
                  label="Created by"
                  anyLabel="Anyone"
                  value={draft.createdById}
                  users={users}
                  onChange={set("createdById")}
                />
                <UserSelect
                  id="approval-updated-by"
                  label="Updated by"
                  anyLabel="Anyone"
                  value={draft.updatedById}
                  users={users}
                  onChange={set("updatedById")}
                />
              </div>
            </FilterSection>

            <FilterSection title="Dates">
              <RangeFieldGroup
                legend="Created on"
                startId="approval-created-after"
                endId="approval-created-before"
                startLabel="After"
                endLabel="Before"
                startValue={draft.createdOnAfter}
                endValue={draft.createdOnBefore}
                onStartChange={set("createdOnAfter")}
                onEndChange={set("createdOnBefore")}
              />
              <RangeFieldGroup
                legend="Updated on"
                startId="approval-updated-after"
                endId="approval-updated-before"
                startLabel="After"
                endLabel="Before"
                startValue={draft.updatedOnAfter}
                endValue={draft.updatedOnBefore}
                onStartChange={set("updatedOnAfter")}
                onEndChange={set("updatedOnBefore")}
              />
            </FilterSection>

            <FilterSection
              title="Classification"
              hint="Why the invoice needs attention, and how it was reviewed."
            >
              <OptionChecklist
                legend="Review type"
                options={options.reviewType}
                selected={draft.reviewTypeIds}
                onToggle={toggleList("reviewTypeIds")}
              />
              <OptionChecklist
                legend="Failure type"
                options={FAILURE_TYPE_OPTIONS.map((option) => ({
                  id: option.value,
                  label: option.label,
                  color: option.color,
                }))}
                selected={draft.failureTypes}
                onToggle={toggleList("failureTypes")}
              />
            </FilterSection>

            {/* Last, because every row in this queue is already Approval
                Required - these narrow within it rather than define it. */}
            <FilterSection title="Status">
              <OptionChecklist
                legend="Review status"
                options={options.review}
                selected={draft.reviewStatusIds}
                onToggle={toggleList("reviewStatusIds")}
              />
              <OptionChecklist
                legend="Processing status"
                options={options.processing}
                selected={draft.processingStatusIds}
                onToggle={toggleList("processingStatusIds")}
              />
            </FilterSection>
          </div>

          <SheetFooter className="filter-offcanvas-footer">
            <Button
              type="button"
              variant="outline"
              className="h-9 flex-1 rounded-[0.8rem] border-line shadow-none"
              onClick={clear}
            >
              Clear
            </Button>
            <Button type="button" className="h-9 flex-1 rounded-[0.8rem]" onClick={apply}>
              Apply filters
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
}
